import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle, Mail, RefreshCw, Clock, LogOut } from 'lucide-react';
import type { ResendStatus, VerifyEmailState } from '../types/verifyEmail';

interface VerifyEmailStatusCardProps {
  state: VerifyEmailState;
  resendStatus: ResendStatus;
  resendError: string;
  userEmail?: string;
  hasUser: boolean;
  onResend: () => void;
  onSignOut: () => void;
  // 'signing-in' is shown in the original sign-up tab right before redirecting
  // through /auth/exchange. 'handoff' is shown in the tab that clicked the
  // verification link, asking the user to head back to where they signed up.
  successMode?: 'signing-in' | 'handoff';
}

const getStatusIcon = (status: VerifyEmailState['status'], successMode?: string) => {
  const iconClasses = 'w-10 h-10 text-white';
  switch (status) {
    case 'awaiting':
      return <Mail className={iconClasses} />;
    case 'loading':
      return <Loader2 className={`${iconClasses} animate-spin`} />;
    case 'success':
      return successMode === 'signing-in' ? (
        <Loader2 className={`${iconClasses} animate-spin`} />
      ) : (
        <CheckCircle className={iconClasses} />
      );
    case 'error':
      return <AlertCircle className={iconClasses} />;
    case 'expired':
      return <Clock className={iconClasses} />;
  }
};

const getIconBackground = (status: VerifyEmailState['status']) => {
  switch (status) {
    case 'awaiting':
    case 'loading':
      return 'var(--color-accent-teal)';
    case 'success':
      return 'var(--color-accent-green)';
    case 'error':
      return 'var(--color-error, #ef4444)';
    case 'expired':
      return 'var(--color-warning, #f59e0b)';
  }
};

const getTitle = (status: VerifyEmailState['status']) => {
  switch (status) {
    case 'awaiting':
      return 'Check your email';
    case 'loading':
      return 'Verifying your email...';
    case 'success':
      return 'Email verified';
    case 'error':
      return 'Verification failed';
    case 'expired':
      return 'Link expired';
  }
};

export const VerifyEmailStatusCard: React.FC<VerifyEmailStatusCardProps> = ({
  state,
  resendStatus,
  resendError,
  userEmail,
  hasUser,
  onResend,
  onSignOut,
  successMode,
}) => {
  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl p-8"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-default)',
      }}
    >
      <div className="flex justify-center mb-6">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: getIconBackground(state.status) }}
        >
          {getStatusIcon(state.status, successMode)}
        </div>
      </div>

      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {getTitle(state.status)}
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {state.message}
        </p>
        {state.status === 'awaiting' && userEmail && (
          <>
            <p
              className="text-base font-semibold mt-2"
              style={{ color: 'var(--color-accent-teal)' }}
            >
              {userEmail}
            </p>
            <p
              className="text-xs leading-relaxed mt-3"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              Click it from any browser. We'll sign you in here when you're done.
            </p>
          </>
        )}
      </div>

      {resendStatus === 'sent' && (
        <div
          className="p-4 rounded-lg border mb-6 flex items-center gap-3"
          style={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderColor: 'rgba(16, 185, 129, 0.3)',
          }}
        >
          <CheckCircle className="w-5 h-5 shrink-0" style={{ color: '#10b981' }} />
          <p className="text-sm font-medium" style={{ color: '#10b981' }}>
            Verification email sent! Check your inbox.
          </p>
        </div>
      )}

      {resendStatus === 'error' && (
        <div
          className="p-4 rounded-lg border mb-6"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderColor: 'rgba(239, 68, 68, 0.3)',
          }}
        >
          <p className="text-sm" style={{ color: '#ef4444' }}>
            {resendError}
          </p>
        </div>
      )}

      {state.status === 'loading' && (
        <div className="space-y-3 mb-6">
          <div
            className="w-full h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: 'var(--color-border-default)' }}
          >
            <div
              className="h-full rounded-full animate-pulse"
              style={{
                backgroundColor: 'var(--color-accent-teal)',
                width: '60%',
              }}
            />
          </div>
          <p className="text-xs text-center" style={{ color: 'var(--color-text-tertiary)' }}>
            This should only take a moment...
          </p>
        </div>
      )}

      {state.status === 'awaiting' && (
        <div className="space-y-3">
          <button
            onClick={onResend}
            disabled={resendStatus === 'sending'}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3"
          >
            {resendStatus === 'sending' ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Resend verification email
              </>
            )}
          </button>

          {hasUser && (
            <button
              onClick={onSignOut}
              className="btn-secondary w-full flex items-center justify-center gap-2 py-3"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          )}
        </div>
      )}

      {state.status === 'error' && (
        <div className="space-y-3">
          <div
            className="p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderColor: 'rgba(239, 68, 68, 0.3)',
            }}
          >
            <p className="text-sm" style={{ color: '#ef4444' }}>
              {state.message}
            </p>
          </div>

          <button
            onClick={onResend}
            disabled={resendStatus === 'sending'}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3"
          >
            {resendStatus === 'sending' ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Request New Link
              </>
            )}
          </button>

          <Link to="/" className="btn-secondary w-full flex items-center justify-center gap-2 py-3">
            Go to Home
          </Link>

          <p className="text-xs text-center" style={{ color: 'var(--color-text-tertiary)' }}>
            If the problem persists, please contact support.
          </p>
        </div>
      )}

      {state.status === 'expired' && (
        <div className="space-y-3">
          <div
            className="p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              borderColor: 'rgba(245, 158, 11, 0.3)',
            }}
          >
            <p className="text-sm" style={{ color: '#f59e0b' }}>
              Verification links expire after 24 hours for security.
            </p>
          </div>

          <button
            onClick={onResend}
            disabled={resendStatus === 'sending'}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3"
          >
            {resendStatus === 'sending' ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Request New Link
              </>
            )}
          </button>

          <Link to="/" className="btn-secondary w-full flex items-center justify-center gap-2 py-3">
            Go to Home
          </Link>
        </div>
      )}

      {state.status === 'awaiting' && (
        <p className="text-xs text-center mt-6" style={{ color: 'var(--color-text-tertiary)' }}>
          Didn't receive the email? Check your spam folder or try resending.
        </p>
      )}
      {state.status !== 'awaiting' && state.status !== 'success' && (
        <p className="text-xs text-center mt-6" style={{ color: 'var(--color-text-tertiary)' }}>
          Having trouble? Contact us at support@autar.ai
        </p>
      )}
    </div>
  );
};
